import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/supabase';
import { FaHeart, FaShoppingCart, FaShareAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import './ViewProduct.css';

const PRODUCT_MEDIA_BASE_URL =
  "https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/product-media";

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) {
    // Default avatar SVG
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
  }
  
  // If already a full URL, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // If data URL, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;
  
  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[ViewProduct] Error resolving avatar URL:', e);
  }

  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
};

// Resolve product image URL for product-media bucket
const resolveProductImageUrl = (image, userId) => {
  if (!image) return "/default-product.png";
  if (typeof image === "string" && /^(http|https):\/\//i.test(image)) return image;
  if (typeof image === "string" && image.startsWith("data:")) return image;

  try {
    const filename = typeof image === "string" ? image : String(image);
    return `${PRODUCT_MEDIA_BASE_URL}/${userId}/${filename}`;
  } catch (_) {
    return "/default-product.png";
  }
};

export default function ViewProduct() {
  // URL shape: /@username/:productId/:shareId/pr (userId is derived from product.user_id)
  const { username, productId, shareId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(0);
  
  // Get translations - hook must be called unconditionally
  const { translations: tRaw } = useLanguage();
  const t = { downloadModal: tRaw?.downloadModal || {} };

  // Validate route - check if this looks like a ViewProduct route
  const isValidViewProductRoute = React.useMemo(() => {
    if (!username || !productId) return false;
    // Check if pathname ends with /pr (product route indicator)
    const pathEndsWithPr = location.pathname.endsWith('/pr');
    // Check if productId looks like a UUID
    const looksLikeUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId);
    // Also check if username starts with @
    const usernameHasAt = username.startsWith('@');
    return pathEndsWithPr || (looksLikeUUID && usernameHasAt);
  }, [username, productId, location.pathname]);

  // Fetch WiniCoin USD rate
  useEffect(() => {
    const fetchWiniRate = async () => {
      try {
        const { data: appSettings, error: settingsError } = await supabase
          .from("app_settings")
          .select("wini_to_usd_rate")
          .limit(1)
          .single();

        if (!settingsError && appSettings) {
          setWiniToUsdRate(appSettings.wini_to_usd_rate || null);
        }
      } catch (err) {
        console.error("Error fetching Wini to USD rate:", err);
      }
    };

    fetchWiniRate();
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('[ViewProduct] Component mounted with params:', { 
      username, 
      productId,
      shareId, 
      isValidViewProductRoute,
      pathname: location.pathname 
    });
    
    if (!productId) {
      console.warn('[ViewProduct] Missing productId - this route requires a product ID');
    }
  }, [username, productId, shareId, isValidViewProductRoute, location.pathname]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[ViewProduct] Starting to load product...', { productId, username, shareId });

        if (!productId) {
          setError('Product ID is required');
          setLoading(false);
          return;
        }

        // First, verify the share exists (optional)
        if (shareId) {
          try {
            // Note: We might not have a shares table for products, but we can skip this check
            console.log('[ViewProduct] ShareId provided:', shareId);
          } catch (shareErr) {
            console.log('[ViewProduct] Error checking share:', shareErr);
            // Continue anyway
          }
        }

        // Fetch product by ID
        console.log('[ViewProduct] Fetching product with ID:', productId);
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .eq('is_active', true)
          .single();

        if (productError) {
          console.error('[ViewProduct] Product fetch error:', productError);
          setError(`Product not found: ${productError.message}`);
          setLoading(false);
          return;
        }

        if (!productData) {
          console.error('[ViewProduct] Product data is null');
          setError('Product not found');
          setLoading(false);
          return;
        }

        console.log('[ViewProduct] Product found:', productData.name);

        // Fetch user profile by user_id
        const profileUserId = productData.user_id;
        console.log('[ViewProduct] Fetching profile with user_id:', profileUserId);
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', profileUserId)
          .single();

        if (profileError || !profileData) {
          console.error('[ViewProduct] Profile not found:', profileError);
          setError('User profile not found');
          setLoading(false);
          return;
        }

        // Verify username matches if provided
        const cleanUsername = username ? username.replace(/^@+/, '') : null;
        if (cleanUsername && profileData.username !== cleanUsername) {
          console.warn('[ViewProduct] Username mismatch. URL username:', cleanUsername, 'Profile username:', profileData.username);
          // Continue anyway - the userId is the source of truth
        }

        setProduct(productData);
        setProfile(profileData);
        console.log('[ViewProduct] Product and profile loaded successfully');
      } catch (err) {
        console.error('[ViewProduct] Error loading product:', err);
        setError(`Failed to load product: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      loadProduct();
    } else {
      setError('Product ID is missing');
      setLoading(false);
    }
  }, [productId, username, shareId]);

  const handleInteraction = (type) => {
    setShowDownloadModal(true);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (product && product.images && product.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (product && product.images && product.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  // Navigate to user profile - check authentication first
  const handleNavigateToProfile = async () => {
    if (!profile?.user_id) return;
    
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.user) {
        // User is authenticated
        // Check if the profile being clicked is the current user's profile
        if (session.user.id === profile.user_id) {
          // It's the authenticated user's own profile - go to /profile (own profile)
          navigate('/profile');
        } else {
          // It's someone else's profile - go to /profile/:uid
          navigate(`/profile/${profile.user_id}`);
        }
      } else {
        // User is not authenticated - navigate to public ViewProfile page
        // Format: /@username/userId/shareId/u
        const profileUsername = profile.username || profile.display_name || 'user';
        const viewShareId = shareId || 'view'; // Use existing shareId or 'view' as placeholder
        navigate(`/@${encodeURIComponent(profileUsername)}/${profile.user_id}/${viewShareId}/u`);
      }
    } catch (error) {
      console.error('[ViewProduct] Error checking auth:', error);
      // On error, default to public ViewProfile page
      const profileUsername = profile.username || profile.display_name || 'user';
      const viewShareId = shareId || 'view';
      navigate(`/@${encodeURIComponent(profileUsername)}/${profile.user_id}/${viewShareId}/u`);
    }
  };

  // Fullscreen modal handlers
  const handleOpenFullscreen = () => {
    setFullscreenImageIndex(currentImageIndex);
    setShowFullscreenModal(true);
  };

  const handleCloseFullscreen = () => {
    setShowFullscreenModal(false);
  };

  const handleFullscreenNext = (e) => {
    e.stopPropagation();
    if (images && images.length > 1) {
      setFullscreenImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const handleFullscreenPrev = (e) => {
    e.stopPropagation();
    if (images && images.length > 1) {
      setFullscreenImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const getDownloadUrl = () => {
    const appStoreUrl = 'https://apps.apple.com/ao/app/iwinto/id6482291656';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.winto.app';

    if (typeof navigator === 'undefined') {
      return appStoreUrl;
    }

    const possibleOpera =
      typeof window !== 'undefined' && window.opera ? window.opera : '';
    const userAgent =
      navigator.userAgent || navigator.vendor || possibleOpera || '';
    const isAndroid = /android/i.test(userAgent);
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isAndroid) {
      return playStoreUrl;
    }

    if (isIOS) {
      return appStoreUrl;
    }

    return appStoreUrl;
  };

  const handleDownloadClick = () => {
    const url = getDownloadUrl();

    if (typeof window === 'undefined') {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Early check: if URL doesn't end with /pr, this might not be a product route
  if (!location.pathname.endsWith('/pr')) {
    console.log('[ViewProduct] URL does not end with /pr, this might not be a product route');
    // Continue anyway - let it try to load
  }

  if (loading) {
    return (
      <div className="view-product-container">
        <div className="view-product-loading">
          <p>Loading product...</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            Debug: username={username}, productId={productId}, shareId={shareId}
          </p>
        </div>
      </div>
    );
  }

  if (error || !product || !profile) {
    return (
      <div className="view-product-container">
        <div className="view-product-error">
          <p>{error || 'Product not found'}</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            Debug: username={username}, productId={productId}, shareId={shareId}
          </p>
          {product && <p style={{ fontSize: '12px', color: '#999' }}>Product exists but profile missing</p>}
          {profile && <p style={{ fontSize: '12px', color: '#999' }}>Profile exists but product missing</p>}
        </div>
      </div>
    );
  }

  const avatarUrl = resolveAvatarUrl(profile.avatar_url, profile.user_id);
  const productUserId = product.user_id;
  const imagesArray = Array.isArray(product.images) && product.images.length > 0 
    ? product.images 
    : (product.thumbnail_url ? [product.thumbnail_url] : []);
  
  // Resolve all image URLs properly
  const images = imagesArray.length > 0 
    ? imagesArray.map(img => {
        // If image is already a full URL, return it as-is
        if (typeof img === "string" && /^(http|https):\/\//i.test(img)) {
          return img;
        }
        // Otherwise resolve it using the helper
        return resolveProductImageUrl(img, productUserId);
      })
    : ["/default-product.png"];
  
  const hasMultipleImages = images.length > 1;
  const currentImage = images[currentImageIndex] || images[0] || "/default-product.png";
  
  const priceInWini = Number(product.price_in_coins || 0);
  const priceInUsd = winiToUsdRate ? priceInWini * winiToUsdRate : null;

  return (
    <div className="view-product-container">
      <div className="view-product-content">
        {/* User Header */}
        <div className="view-product-header">
          <img
            src={avatarUrl}
            alt={profile.username || profile.display_name}
            className="view-product-avatar view-product-avatar-clickable"
            onClick={handleNavigateToProfile}
          />
          <span 
            className="view-product-username view-product-username-clickable"
            onClick={handleNavigateToProfile}
          >
            {profile.display_name || profile.username || 'User'}
          </span>
        </div>

        {/* Product Images Section */}
        <div className="view-product-media-wrapper">
          <div className="view-product-media-container">
            {hasMultipleImages && (
              <button
                className="view-product-carousel-arrow view-product-carousel-arrow-left"
                onClick={handlePrevImage}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
            )}
            
            <img
              src={currentImage}
              alt={product.name || "Product"}
              className="view-product-media view-product-media-clickable"
              onClick={handleOpenFullscreen}
              onError={(e) => {
                e.currentTarget.src = "/default-product.png";
              }}
            />

            {hasMultipleImages && (
              <button
                className="view-product-carousel-arrow view-product-carousel-arrow-right"
                onClick={handleNextImage}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
          
          {/* Image indicators */}
          {hasMultipleImages && (
            <div className="view-product-indicators">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`view-product-indicator ${index === currentImageIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info Section */}
        <div className="view-product-info">
          {/* Price */}
          <div className="view-product-price">
            {priceInUsd !== null ? (
              <>
                <span className="view-product-price-usd">${priceInUsd.toFixed(2)}</span>
                {/* <span className="view-product-price-wini"> • {priceInWini.toFixed(2)} Wini</span> */}
              </>
            ) : (
              <span className="view-product-price-wini">{priceInWini.toFixed(2)} Wini</span>
            )}
          </div>

          {/* Product Title */}
          <h2 className="view-product-title">{product.name || "Product"}</h2>

          {/* Product Description */}
          {product.description && (
            <p className="view-product-description">{product.description}</p>
          )}
        </div>

        {/* Interaction Buttons */}
        <div className="view-product-actions">
          <button
            className="view-product-action-btn"
            onClick={() => handleInteraction('like')}
            aria-label="Like"
          >
            <FaHeart />
          </button>
          <button
            className="view-product-action-btn"
            onClick={() => handleInteraction('cart')}
            aria-label="Add to cart"
          >
            <FaShoppingCart />
          </button>
          <button
            className="view-product-action-btn"
            onClick={() => handleInteraction('share')}
            aria-label="Share"
          >
            <FaShareAlt />
          </button>
        </div>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="view-product-modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="view-product-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="view-product-modal-title">
              {t?.downloadModal?.title || "Want to interact with this product?"}
            </h2>
            <p className="view-product-modal-subtitle">
              {t?.downloadModal?.subtitle || "Download the iWinto app to like, add to cart, and purchase!"}
            </p>
            <div className="view-product-store-buttons">
              <button
                type="button"
                className="view-product-store-btn"
                onClick={handleDownloadClick}
              >
                {t?.downloadModal?.downloadApp || "Download the app"}
              </button>
            </div>
            <button
              className="view-product-modal-close"
              onClick={() => setShowDownloadModal(false)}
            >
              {t?.downloadModal?.close || "Close"}
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen Media Modal */}
      {showFullscreenModal && images.length > 0 && (
        <div className="view-product-fullscreen-overlay" onClick={handleCloseFullscreen}>
          <button 
            className="view-product-fullscreen-close-btn"
            onClick={handleCloseFullscreen}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          
          <div className="view-product-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && (
              <button
                className="view-product-fullscreen-arrow view-product-fullscreen-arrow-left"
                onClick={handleFullscreenPrev}
                aria-label="Previous"
              >
                <FaChevronLeft />
              </button>
            )}
            
            <img
              src={images[fullscreenImageIndex] || "/default-product.png"}
              alt={product?.name || "Product"}
              className="view-product-fullscreen-media"
              onError={(e) => {
                e.currentTarget.src = "/default-product.png";
              }}
            />
            
            {images.length > 1 && (
              <button
                className="view-product-fullscreen-arrow view-product-fullscreen-arrow-right"
                onClick={handleFullscreenNext}
                aria-label="Next"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
          
          {/* Image indicators */}
          {images.length > 1 && (
            <div className="view-product-fullscreen-indicators">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`view-product-fullscreen-indicator ${index === fullscreenImageIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

